import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import HindiNotifyPanel, { useHindiNotify } from './HindiNotifyPanel';
import PaceAnalytics from './PaceAnalytics';
import QuantDashboard from './QuantDashboard';
import { useSessionKeepAlive, SessionBanner } from './SessionKeepAlive';
import ServerErrorBanner, { useServerErrors } from './ServerErrorBanner';
import { formatIstDateKey, formatActivityWhen } from './ist-datetime';
import './OptionsTrading.css';

const STATUS_LABELS = {
  running: 'Trading live',
  waiting: 'Waiting for market',
  target_hit: 'Target hit — stopped',
  max_loss_hit: 'Max loss — stopped',
  loss_protection_stop: 'Loss protection — stopped',
  market_closed: 'Market closed',
  weekend: 'Weekend — closed',
  disabled: 'Disabled',
  idle: 'Starting…'
};

const STATUS_PILL = {
  running: 'live',
  waiting: 'closed',
  target_hit: 'gold',
  max_loss_hit: 'stop',
  loss_protection_stop: 'stop',
  market_closed: 'closed',
  weekend: 'closed',
  disabled: 'closed',
  idle: 'closed'
};

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const OptionsTrading = () => {
  const [prices, setPrices] = useState([]);
  const [portfolio, setPortfolio] = useState({});
  const [trades, setTrades] = useState([]);
  const [autoStatus, setAutoStatus] = useState(null);
  const [settings, setSettings] = useState({
    symbol: '^NSEI',
    underlyingName: 'NIFTY',
    optionType: 'auto',
    lots: 1,
    lotSize: 50,
    dailyTarget: 100,
    maxDailyInvestment: 100,
    maxDailyLoss: 0,
    stopLossPerTrade: 18,
    relentlessTarget: true,
    triggerPct: 0.25,
    pollSeconds: 30,
    autoStartAtMarketOpen: true,
    autoOpenBrowser: true,
    enabled: true
  });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [dailyHistory, setDailyHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('today');
  const { errors: serverErrors, pushError, clearSource, dismiss: dismissServerError, clearAll: clearAllServerErrors } = useServerErrors();

  const symbols = ['^NSEI', 'RELIANCE.NS', 'TCS.NS', 'HDFCBANK.NS', 'INFY.NS'];

  const refresh = useCallback(async () => {
    try {
      const [priceRes, portRes, logRes, statusRes, todayRes, historyRes] = await Promise.all([
        axios.get(`/api/quote?symbols=${symbols.join(',')}`),
        axios.get('/api/portfolio'),
        axios.get('/api/logs'),
        axios.get('/api/automation/status'),
        axios.get('/api/daily/today'),
        axios.get('/api/daily/history?limit=30')
      ]);
      setPrices(priceRes.data);
      setPortfolio(portRes.data);
      setTrades(logRes.data.trades || []);
      setAutoStatus(statusRes.data);
      setDailyReport(todayRes.data);
      setDailyHistory(historyRes.data);
      clearSource('refresh');
    } catch (e) {
      pushError('refresh', e);
    }
  }, [pushError, clearSource]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get('/api/automation/settings');
      setSettings(res.data);
      await axios.post('/api/automation/enable');
      clearSource('settings');
    } catch (e) {
      pushError('settings', e);
    }
  }, [pushError, clearSource]);

  useEffect(() => {
    loadSettings();
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [loadSettings, refresh]);

  const saveSettings = async (updated) => {
    setSettings(updated);
    try {
      await axios.post('/api/automation/settings', updated);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
      clearSource('save-settings');
      refresh();
    } catch (e) {
      pushError('save-settings', e);
    }
  };

  const updateSetting = (key, value) => {
    saveSettings({ ...settings, [key]: value });
  };

  const totalDay = autoStatus?.totalDayPnl ?? 0;
  const target = settings.dailyTarget || 100;
  const progress = autoStatus?.progressPct ?? 0;
  const pos = autoStatus?.currentPosition;
  const today = dailyReport?.today || autoStatus?.dailyJournal || {};
  const summary = dailyHistory?.summary || {};
  const status = autoStatus?.status || 'idle';

  const nowBanner = useMemo(() => {
    if (status === 'target_hit') {
      return { className: 'target-hit', text: `Daily target of ${fmt(target)} reached. Bot stopped for today.` };
    }
    if (status === 'loss_protection_stop') {
      return { className: 'stopped', text: 'Loss protection triggered — trading paused to limit today\'s downside.' };
    }
    if (status === 'max_loss_hit') {
      return { className: 'stopped', text: 'Maximum daily loss reached — bot stopped for today.' };
    }
    if (status !== 'target_hit' && settings.relentlessTarget !== false && status === 'running') {
      return {
        className: 'relentless',
        text: `Bot keeps trading until ${fmt(target)} profit target is hit (won't stop early on small losses).`
      };
    }
    return {
      className: '',
      text: autoStatus?.lastAction || 'Initializing automation…'
    };
  }, [status, settings.relentlessTarget, target, autoStatus?.lastAction]);

  useHindiNotify(autoStatus?.activityLog);
  useSessionKeepAlive();

  return (
    <div className="opt-app">
      <header className="opt-topbar">
        <div className="opt-topbar-left">
          <h1>Options Trading</h1>
          <p>Paper NIFTY options · auto CE/PE · daily profit target</p>
        </div>
        <div className="opt-topbar-right">
          <Link to="/ledger/options" className="opt-ledger-link">Ledger</Link>
          <span className={`opt-status-pill ${STATUS_PILL[status] || 'closed'}`}>
            {STATUS_LABELS[status] || 'Loading'}
          </span>
        </div>
      </header>

      <SessionBanner />

      <ServerErrorBanner
        errors={serverErrors}
        onDismiss={dismissServerError}
        onClearAll={clearAllServerErrors}
      />

      <section className="opt-kpi-row" aria-label="Today's summary">
        <div className="opt-kpi">
          <div className="opt-kpi-label">Today&apos;s P&amp;L</div>
          <div className={`opt-kpi-value ${totalDay > 0 ? 'up' : totalDay < 0 ? 'down' : 'neutral'}`}>
            {fmt(totalDay)}
          </div>
          <div className="opt-kpi-sub">
            Realized {fmt(autoStatus?.dailyPnl ?? 0)} · Unrealized {fmt(autoStatus?.unrealizedPnl ?? 0)}
          </div>
        </div>
        <div className="opt-kpi">
          <div className="opt-kpi-label">Profit target</div>
          <div className={`opt-kpi-value ${totalDay >= target ? 'up' : 'neutral'}`}>
            {Math.min(100, Math.round(progress))}%
          </div>
          <div className="opt-kpi-sub">{fmt(totalDay)} of {fmt(target)}</div>
          <div className="opt-progress">
            <div
              className={`opt-progress-fill ${totalDay >= target ? 'done' : ''}`}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        </div>
        <div className="opt-kpi">
          <div className="opt-kpi-label">Budget left today</div>
          <div className="opt-kpi-value neutral">
            {fmt(autoStatus?.investmentRemaining ?? settings.maxDailyInvestment ?? 50)}
          </div>
          <div className="opt-kpi-sub">
            Cap {fmt(settings.maxDailyInvestment ?? 50)}/day · {portfolio.tradesCount ?? 0} trades
          </div>
        </div>
      </section>

      <div className={`opt-now-banner ${nowBanner.className}`}>
        <strong>What&apos;s happening now</strong>
        {nowBanner.text}
      </div>

      <nav className="opt-tabs" aria-label="Options sections">
        <button type="button" className={`opt-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>
          Today
        </button>
        <button type="button" className={`opt-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
        <button type="button" className={`opt-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          History
        </button>
      </nav>

      {activeTab === 'today' && (
        <>
          <div className="opt-grid-2">
            <div className="opt-card">
              <h3>Open position</h3>
              {pos ? (
                <>
                  <div className="opt-position-title">
                    {settings.underlyingName} {pos.strike} {pos.optionType}
                  </div>
                  <div className="opt-stat-grid">
                    <div className="opt-stat">
                      <span>Entry premium</span>
                      <strong>{fmt(pos.entryPremium)}</strong>
                    </div>
                    <div className="opt-stat">
                      <span>Entry spot</span>
                      <strong>{fmt(pos.entrySpot)}</strong>
                    </div>
                    <div className="opt-stat">
                      <span>Units</span>
                      <strong>{pos.units ?? pos.lots}</strong>
                    </div>
                    <div className="opt-stat">
                      <span>Unrealized P&amp;L</span>
                      <strong className={(autoStatus?.unrealizedPnl ?? 0) >= 0 ? 'up' : 'down'}>
                        {fmt(autoStatus?.unrealizedPnl ?? 0)}
                      </strong>
                    </div>
                  </div>
                </>
              ) : (
                <div className="opt-position-empty">
                  No open position — watching for {settings.triggerPct}% move from day open
                  {autoStatus?.dayOpenPrice ? ` (open ${fmt(autoStatus.dayOpenPrice)})` : ''}
                </div>
              )}
            </div>

            <div className="opt-card">
              <h3>Live prices</h3>
              <div className="opt-ticker">
                {prices.length > 0 ? prices.map((p) => (
                  <div key={p.symbol} className="opt-ticker-chip">
                    <span className="sym">{p.symbol.replace('.NS', '').replace('^', '')}</span>
                    <span className="px">{fmt(p.price)}</span>
                    <span className={`chg ${p.change >= 0 ? 'up' : 'down'}`}>
                      {p.change >= 0 ? '+' : ''}{p.change.toFixed(2)}%
                    </span>
                  </div>
                )) : (
                  <span className="opt-kpi-sub">Fetching quotes…</span>
                )}
              </div>
              <div className="opt-stat-grid" style={{ marginTop: 14 }}>
                <div className="opt-stat">
                  <span>Paper cash</span>
                  <strong>{fmt(portfolio.cash ?? 0)}</strong>
                </div>
                <div className="opt-stat">
                  <span>Spot (bot)</span>
                  <strong>{fmt(autoStatus?.lastPrice ?? 0)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="opt-card">
            <h3>Live activity</h3>
            <div className="opt-log">
              {(autoStatus?.activityLog || []).slice(0, 12).map((entry, i) => (
                <div key={i} className="opt-log-line">
                  <span className="opt-log-time">{formatActivityWhen(entry)}</span>
                  {entry.message}
                </div>
              ))}
              {(!autoStatus?.activityLog || autoStatus.activityLog.length === 0) && (
                <div className="opt-position-empty">Waiting for market activity…</div>
              )}
            </div>
          </div>

          <HindiNotifyPanel
            activityLog={autoStatus?.activityLog}
            lastAction={autoStatus?.lastAction}
            lastHindiAlert={autoStatus?.lastHindiAlert}
          />

          <details className="opt-details">
            <summary>Advanced analytics</summary>
            <div className="opt-details-body">
              <PaceAnalytics analytics={autoStatus?.analytics} sessionPhase={autoStatus?.sessionPhase} />
              <QuantDashboard
                mode="options"
                analytics={autoStatus?.analytics}
                sessionPhase={autoStatus?.sessionPhase}
                optionsQuant={autoStatus?.quant}
                niftyChange={prices.find((p) => p.symbol === '^NSEI')?.change ?? 0}
              />
            </div>
          </details>
        </>
      )}

      {activeTab === 'settings' && (
        <div className="opt-card">
          <h3>Automation settings <span className="opt-card-hint">— saves automatically</span></h3>
          {settingsSaved && <span className="opt-saved">Saved</span>}

          <div className="opt-settings-section">
            <h4>Market</h4>
            <div className="opt-field-row">
              <div className="opt-field">
                <label htmlFor="opt-underlying">Underlying</label>
                <select
                  id="opt-underlying"
                  value={settings.symbol}
                  onChange={(e) => {
                    const map = { '^NSEI': 'NIFTY', 'RELIANCE.NS': 'RELIANCE', 'TCS.NS': 'TCS', 'HDFCBANK.NS': 'HDFCBANK', 'INFY.NS': 'INFY' };
                    saveSettings({ ...settings, symbol: e.target.value, underlyingName: map[e.target.value] || 'NIFTY' });
                  }}
                >
                  <option value="^NSEI">NIFTY</option>
                  <option value="RELIANCE.NS">RELIANCE</option>
                  <option value="TCS.NS">TCS</option>
                  <option value="HDFCBANK.NS">HDFCBANK</option>
                  <option value="INFY.NS">INFY</option>
                </select>
              </div>
              <div className="opt-field">
                <label htmlFor="opt-type">Option type</label>
                <select
                  id="opt-type"
                  value={settings.optionType}
                  onChange={(e) => updateSetting('optionType', e.target.value)}
                >
                  <option value="auto">Auto (CE if up, PE if down)</option>
                  <option value="CE">Call (CE) only</option>
                  <option value="PE">Put (PE) only</option>
                </select>
              </div>
              <div className="opt-field">
                <label htmlFor="opt-trigger">Entry trigger %</label>
                <input
                  id="opt-trigger"
                  type="number"
                  step="0.05"
                  value={settings.triggerPct}
                  onChange={(e) => updateSetting('triggerPct', parseFloat(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="opt-settings-section">
            <h4>Targets &amp; risk</h4>
            <div className="opt-field-row">
              <div className="opt-field">
                <label htmlFor="opt-target">Daily profit target ₹</label>
                <input
                  id="opt-target"
                  type="number"
                  value={settings.dailyTarget}
                  onChange={(e) => updateSetting('dailyTarget', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-invest">Max invest / day ₹</label>
                <input
                  id="opt-invest"
                  type="number"
                  value={settings.maxDailyInvestment}
                  onChange={(e) => updateSetting('maxDailyInvestment', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-stop">Stop loss / trade ₹</label>
                <input
                  id="opt-stop"
                  type="number"
                  value={settings.stopLossPerTrade}
                  onChange={(e) => updateSetting('stopLossPerTrade', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-maxloss">Max loss / day ₹</label>
                <input
                  id="opt-maxloss"
                  type="number"
                  value={settings.maxDailyLoss}
                  onChange={(e) => updateSetting('maxDailyLoss', parseFloat(e.target.value))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-lots">Lots</label>
                <input
                  id="opt-lots"
                  type="number"
                  value={settings.lots}
                  onChange={(e) => updateSetting('lots', parseInt(e.target.value, 10))}
                />
              </div>
              <div className="opt-field">
                <label htmlFor="opt-lotsize">Lot size</label>
                <input
                  id="opt-lotsize"
                  type="number"
                  value={settings.lotSize}
                  onChange={(e) => updateSetting('lotSize', parseInt(e.target.value, 10))}
                />
              </div>
            </div>
          </div>

          <div className="opt-settings-section">
            <h4>Schedule</h4>
            <div className="opt-checks">
              <label>
                <input
                  type="checkbox"
                  checked={settings.autoStartAtMarketOpen}
                  onChange={(e) => updateSetting('autoStartAtMarketOpen', e.target.checked)}
                />
                Auto-start at 9:15 AM IST
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.autoOpenBrowser}
                  onChange={(e) => updateSetting('autoOpenBrowser', e.target.checked)}
                />
                Open this screen at market open
              </label>
            </div>
          </div>

          <p className="opt-settings-note">
            Bot invests up to <strong>{fmt(settings.maxDailyInvestment)}</strong> per day and stops when profit hits{' '}
            <strong>{fmt(settings.dailyTarget)}</strong>. Polls every {settings.pollSeconds}s during market hours (9:15–3:30 IST).
          </p>
        </div>
      )}

      {activeTab === 'history' && (
        <>
          <div className="opt-card">
            <h3>Today — {today.date ? formatIstDateKey(today.date) : '—'}</h3>
            <div className="opt-stat-grid">
              <div className="opt-stat">
                <span>Invested</span>
                <strong>{fmt(today.totalInvested ?? 0)}</strong>
              </div>
              <div className="opt-stat">
                <span>Returned</span>
                <strong>{fmt(today.totalReturned ?? 0)}</strong>
              </div>
              <div className="opt-stat">
                <span>Net P&amp;L</span>
                <strong className={(today.netPnl ?? 0) >= 0 ? 'up' : 'down'}>{fmt(today.netPnl ?? 0)}</strong>
              </div>
              <div className="opt-stat">
                <span>Trades</span>
                <strong>{today.tradesCount ?? 0} ({today.winCount ?? 0}W / {today.lossCount ?? 0}L)</strong>
              </div>
            </div>
            <div className="opt-lifetime">
              <strong>Lifetime:</strong> invested {fmt(summary.totalInvested ?? 0)} · net {fmt(summary.netPnl ?? 0)} ·
              target hit {summary.targetHitDays ?? 0} days · win/loss days {summary.winDays ?? 0}/{summary.lossDays ?? 0}
            </div>
          </div>

          <div className="opt-card">
            <h3>Past days</h3>
            <div className="opt-table-wrap">
              <table className="opt-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invested</th>
                    <th>Returned</th>
                    <th>Net P&amp;L</th>
                    <th>Trades</th>
                    <th>Target</th>
                  </tr>
                </thead>
                <tbody>
                  {(dailyHistory?.history || []).map((day, i) => (
                    <tr key={i} className={day.netPnl >= day.target ? 'row-win' : day.netPnl < 0 ? 'row-loss' : ''}>
                      <td>{day.dateTime || formatIstDateKey(day.date)}</td>
                      <td>{fmt(day.totalInvested)}</td>
                      <td>{fmt(day.totalReturned)}</td>
                      <td>{fmt(day.netPnl)}</td>
                      <td>{day.tradesCount}</td>
                      <td>{day.targetHit ? 'Yes' : '—'}</td>
                    </tr>
                  ))}
                  {(!dailyHistory?.history || dailyHistory.history.length === 0) && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: 'var(--opt-muted)' }}>
                        No past days yet — history appears after trading sessions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="opt-card">
            <h3>Trade log</h3>
            <div className="opt-log">
              {trades.length > 0 ? trades.slice(-20).reverse().map((t, i) => (
                <div key={i} className="opt-log-line">
                  <span className="opt-log-time">{t.time}</span>
                  {t.type} {t.symbol} ×{t.qty} @ {fmt(t.price)}
                  {t.pnl !== 0 && ` · P&L ${fmt(t.pnl)}`}
                </div>
              )) : (
                <div className="opt-position-empty">No trades yet — bot trades automatically when market is open</div>
              )}
            </div>
          </div>
        </>
      )}

      <p className="opt-footer">
        Paper options only — no real broker. Run <code>start-trading.bat</code> before market open.
      </p>
    </div>
  );
};

export default OptionsTrading;
